<template>
  <div class="app-tabs">
    <div class="app-tabs__header-wrapper">
      <span v-if="hasPrev && isMobile" class="app-tabs__arrow left" @click="prevTab">
        <AppIcon icon="fas fa-caret-left" />
      </span>
      <ul class="app-tabs__header">
        <li
          v-for="(tab, index) in tabsItems"
          :key="`${tab}-${index}`"
          :class="activeClass(tab)"
          :data-tab="tabSlugName(tabsNames[index])"
          class="app-tabs__header-item"
          @click="selectTab(tab)"
        >
          {{ tabsNames[index] }}
        </li>
      </ul>
      <span v-if="hasNext && isMobile" class="app-tabs__arrow right" @click="nextTab">
        <AppIcon icon="fas fa-caret-right" />
      </span>
    </div>
    <!-- tabs -->
    <div class="app-tabs__wrapper">
      <transition name="fade-tab" tag="div" mode="out-in">
        <slot :name="activeTab" />
      </transition>
    </div>
  </div>
</template>

<script>
// Utilities
import { slugify } from 'Utils'
import MobileBreakpointControl from 'Mixins/MobileBreakpointControl'

export default {
  name: 'AppTabs',

  components: {
    AppIcon: () => import('Ui/AppIcon'),
  },

  props: {
    currentTab: {
      type: String,
      default: '',
    },

    tabsNames: {
      type: Array,
      required: true,
    },

    routerHash: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [MobileBreakpointControl],

  data() {
    return {
      tabsItems: null,
      activeTab: this.currentTab.trim(),
    }
  },

  methods: {
    async scrollToCurrentTab() {
      const tabHeaders = this.$el.querySelector('.app-tabs__header')
      await this.$nextTick(() => {
        const activeTab = tabHeaders.querySelector('.app-tabs__header-item.active')
        // scroll into tab
        tabHeaders && tabHeaders.scrollTo(activeTab.offsetLeft, 0)
      })
    },

    selectTab(currentTab) {
      if (this.routerHash) {
        this.activeTab = currentTab || this.cleanHash
        history.pushState({}, '', `#${currentTab || this.cleanHash}`)
      } else {
        this.activeTab = currentTab
      }
      this.scrollToCurrentTab()
    },

    prevTab() {
      const currentTabIdx = this.tabsItems.indexOf(this.activeTab)
      this.selectTab(this.tabsItems[currentTabIdx - 1])
    },

    nextTab() {
      const currentTabIdx = this.tabsItems.indexOf(this.activeTab)
      this.selectTab(this.tabsItems[currentTabIdx + 1])
    },
  },

  computed: {
    cleanHash() {
      return window.location.hash.replace('#', '')
    },

    activeClass() {
      return (tab) => {
        return this.activeTab === tab && 'active'
      }
    },

    tabSlugName() {
      return (tabName) => {
        return slugify(tabName)
      }
    },

    hasPrev() {
      return this.tabsItems?.indexOf(this.activeTab) - 1 >= 0
    },

    hasNext() {
      return this.tabsItems?.indexOf(this.activeTab) + 1 <= this.tabsItems?.length - 1
    },
  },

  mounted() {
    // setting neccesary events
    // into view event ⤵
    this.isMobile && window.addEventListener('load', this.scrollToCurrentTab)
    // getting tabs from slot
    this.tabsItems = Object.keys(this.$slots)
    if (this.routerHash) {
      const currentHash = this.tabsItems.includes(this.cleanHash) ? this.cleanHash : this.currentTab
      this.selectTab(currentHash)
    }
  },

  beforeDestroy() {
    window.removeEventListener('load', this.scrollToCurrentTab)
  },
}
</script>

<style scoped>
.app-tabs__header-wrapper {
  position: relative;
  z-index: 0;
}

.app-tabs__header {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-auto-flow: column;
  justify-content: space-between;
  position: relative;
  text-align: center;
  list-style: none;
  padding: 0 20px;
  overflow-x: auto;
}

.app-tabs__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  position: absolute;
  top: 0;
  width: 30px;
  height: 100%;
  z-index: 2;
}

.app-tabs__arrow.left {
  left: 10px;
}

.app-tabs__arrow.right {
  right: 10px;
}

.app-tabs__header-item {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  width: 100%;
  color: var(--c-primary);
  padding: 10px 20px;
  white-space: nowrap;
  user-select: none;
  cursor: pointer;
}

.app-tabs__header-item.active {
  background-color: #fff;
  z-index: 1;
}

.app-tabs__wrapper {
  position: relative;
  overflow: hidden;
}

@media (max-width: 680px) {
  .app-tabs__header {
    grid-template-columns: 100% 100%;
  }

  .app-tabs__header-item {
    width: 100%;
  }
}

/* fade-tab */
.fade-tab-enter-active,
.fade-tab-leave-active {
  transition: opacity 0.3s, transform 0.35s;
}

.fade-tab-enter {
  opacity: 0;
  transform: translateY(5%);
}

.fade-tab-leave-to {
  opacity: 0;
  transform: translateY(-20%);
}
</style>
